/**
 * Laser-wall patterns 4–7 (legacy Avoid! attacks #4–#7, family `lasers`;
 * docs/legacy/AVOID_LEGACY_ANALYSIS.md §1.3).
 *
 * A laser wall is a horizontal beam that slides across the play area while a laser
 * fires along it. The four legacy variants differ on two axes:
 *  - LANE: the beam runs along the TOP edge or the BOTTOM edge.
 *  - DIRECTION: it sweeps left→right or right→left.
 *  - WHEN THE LASER IS LIVE: "no-start" variants fly in with the laser OFF (a longer
 *    telegraph — you get a free read on the lane), "start" variants are LIVE from
 *    the fly-in (a much shorter telegraph — the lane is dangerous immediately).
 *
 * | id            | lane   | direction | laser on fly-in | telegraph |
 * |---------------|--------|-----------|-----------------|-----------|
 * | LASER_WALL_1  | top    | L→R       | OFF (no-start)  | long      |
 * | LASER_WALL_2  | bottom | R→L       | ON  (start)     | short     |
 * | LASER_WALL_3  | top    | L→R       | ON  (start)     | short     |
 * | LASER_WALL_4  | bottom | R→L       | OFF (no-start)  | long      |
 *
 * The beam is a {@link import('../types').SegmentHazard}: the world stepper sweeps
 * the segment's x across the play area over its active window; the renderer fades the
 * laser in over the telegraph. Coordinates are VIEWPORT-RELATIVE.
 *
 * NOTE: pure core module — never import Phaser, React, or Firebase here.
 */
import type { Hazard, SegmentHazard } from '../types';
import type { Pattern, PatternContext } from './types';

/** Lane offset from the edge, as a fraction of viewport height. */
const LANE_INSET_FRACTION = 0.16;

/**
 * Vertical extent of the lethal beam (collision thickness), as a fraction of height.
 * A concentrated lance like the other laser attacks — NOT a thick wall-band.
 */
const BEAM_THICKNESS_FRACTION = 0.08;

/** Horizontal overshoot past each edge so the beam clears the screen, fraction of width. */
const SWEEP_OVERSHOOT_FRACTION = 0.2;

interface LaserWallSpec {
  /** true = beam runs along the top lane, false = bottom lane. */
  top: boolean;
  /** true = sweep left→right, false = right→left. */
  leftToRight: boolean;
  /** true = laser live during the fly-in (short telegraph); false = off (long telegraph). */
  liveOnFlyIn: boolean;
}

/**
 * Build the single sweeping beam for a laser-wall attack. The segment spans the
 * full viewport width (a horizontal bar in its lane) and the stepper translates it
 * across over the active window; the telegraph encodes the fly-in.
 */
function makeLaserWall(ctx: PatternContext, spec: LaserWallSpec): Hazard[] {
  const { viewport, difficulty, elapsedMs } = ctx;
  const { width, height } = viewport;

  // "start" variants are lethal almost immediately (a brief telegraph just to read
  // the lane); "no-start" variants give a full attackSpeed read before going live.
  const telegraphMs = spec.liveOnFlyIn
    ? difficulty.attackSpeedMs * 0.35
    : difficulty.attackSpeedMs;
  // The sweep across the screen IS the lethal window.
  const activeMs = difficulty.attackSpeedMs;

  const laneY = spec.top
    ? height * LANE_INSET_FRACTION
    : height * (1 - LANE_INSET_FRACTION);
  const thickness = height * BEAM_THICKNESS_FRACTION;
  const overshoot = width * SWEEP_OVERSHOOT_FRACTION;

  // The beam is a horizontal segment spanning the width; the stepper sweeps its x
  // from the entry edge to the far edge. We encode the START pose here (offscreen on
  // the entry side) — telegraph fly-in and active sweep are driven by the stepper.
  const startX1 = spec.leftToRight ? -width - overshoot : width + overshoot;
  const startX2 = spec.leftToRight ? -overshoot : 2 * width + overshoot;

  // Sweep the whole beam across the lane over its lifetime (telegraph fly-in +
  // lethal active sweep) — the generic segment velocity in the world stepper performs
  // the motion, so no laser-specific code lives in the stepper.
  const sweepVx =
    ((spec.leftToRight ? 1 : -1) * 2 * (width + overshoot)) / ((telegraphMs + activeMs) / 1000);

  const beam: SegmentHazard = {
    id: 0,
    family: 'lasers',
    kind: 'segment',
    bornMs: elapsedMs,
    telegraphMs,
    activeMs,
    x1: startX1,
    y1: laneY,
    x2: startX2,
    y2: laneY,
    thickness,
    vx: sweepVx,
  };

  return [beam];
}

/** Attack #4 — laser wall top, L→R, laser OFF on fly-in. */
export const LASER_WALL_1: Pattern = {
  id: 'LASER_WALL_1',
  family: 'lasers',
  spawn: (ctx) => makeLaserWall(ctx, { top: true, leftToRight: true, liveOnFlyIn: false }),
};

/** Attack #5 — laser wall bottom, R→L, laser LIVE immediately. */
export const LASER_WALL_2: Pattern = {
  id: 'LASER_WALL_2',
  family: 'lasers',
  spawn: (ctx) => makeLaserWall(ctx, { top: false, leftToRight: false, liveOnFlyIn: true }),
};

/** Attack #6 — laser wall top, L→R, laser LIVE from the start of the fly-in. */
export const LASER_WALL_3: Pattern = {
  id: 'LASER_WALL_3',
  family: 'lasers',
  spawn: (ctx) => makeLaserWall(ctx, { top: true, leftToRight: true, liveOnFlyIn: true }),
};

/** Attack #7 — laser wall bottom, R→L, laser OFF during fly-in. */
export const LASER_WALL_4: Pattern = {
  id: 'LASER_WALL_4',
  family: 'lasers',
  spawn: (ctx) => makeLaserWall(ctx, { top: false, leftToRight: false, liveOnFlyIn: false }),
};

/* ------------------------------------------------------------------------- *
 * Sweeping laser (owner request): a FULL-HEIGHT vertical beam that sweeps all
 * the way across the field, but BLINKS OFF on a steady beat — so the player
 * watches the rhythm and dashes across (or lets it pass) during an off beat.
 * Distinct from the horizontal laser walls above: this one spans top→bottom and
 * crosses the whole width, gated by {@link import('../types').SegmentHazard}'s
 * blink fields (the world stepper makes it harmless on the off beats).
 * ------------------------------------------------------------------------- */

/** Vertical sweep-beam collision width, as a fraction of viewport width. */
const SWEEP_THICKNESS_FRACTION = 0.07;
/** How far past each side edge the beam starts/ends, as a fraction of width. */
const SWEEP_OVERSHOOT_X_FRACTION = 0.12;
/**
 * Blink cadence, SLOW with LOTS of off time so there's always a comfortable dodge
 * window — and varied per spawn (seeded) so the rhythm changes each appearance.
 * Period is long; the LETHAL (on) slice is short, so off ≫ on every cycle.
 */
const SWEEP_BLINK_PERIOD_MIN_MS = 2000;
const SWEEP_BLINK_PERIOD_VAR_MS = 1100; // → period in [2.0s, 3.1s]
const SWEEP_BLINK_ON_MIN_MS = 430;
const SWEEP_BLINK_ON_VAR_MS = 330; // → lethal slice in [0.43s, 0.76s] (off = the rest)

/**
 * Build a full-height vertical beam that sweeps horizontally across the field while
 * blinking off on a SLOW beat. `leftToRight` enters from the left and exits right
 * (else mirror). The brief telegraph reads the lane; the sweep across IS the lethal
 * window — minus the generous off beats, which core makes harmless. The sweep speed
 * and the blink rhythm are both drawn from the seeded stream so each appearance is
 * a different (but always dodgeable) pattern.
 */
function makeLaserSweep(ctx: PatternContext, leftToRight: boolean): Hazard[] {
  const { viewport, difficulty, rng, elapsedMs } = ctx;
  const { width, height } = viewport;

  const telegraphMs = difficulty.attackSpeedMs * 0.5;
  // Slow sweep (varied per spawn) so there's time to read the blink and cross.
  const sweepScale = 2.6 + rng() * 1.6; // [2.6, 4.2]× attack speed
  const activeMs = difficulty.attackSpeedMs * sweepScale;

  // Seeded blink variation: long period, short lethal slice ⇒ lots of off time.
  const blinkPeriodMs = SWEEP_BLINK_PERIOD_MIN_MS + rng() * SWEEP_BLINK_PERIOD_VAR_MS;
  const blinkDutyOn = SWEEP_BLINK_ON_MIN_MS + rng() * SWEEP_BLINK_ON_VAR_MS;

  const thickness = width * SWEEP_THICKNESS_FRACTION;
  const overshoot = width * SWEEP_OVERSHOOT_X_FRACTION;

  // Start just off the entry edge; sweep the full width (+ overshoot) over the
  // beam's whole life (telegraph fly-in + active sweep) via the generic segment drift.
  const startX = leftToRight ? -overshoot : width + overshoot;
  const span = width + 2 * overshoot;
  const sweepVx = ((leftToRight ? 1 : -1) * span) / ((telegraphMs + activeMs) / 1000);

  const beam: SegmentHazard = {
    id: 0,
    family: 'lasers',
    kind: 'segment',
    bornMs: elapsedMs,
    telegraphMs,
    activeMs,
    x1: startX,
    y1: 0,
    x2: startX,
    y2: height,
    thickness,
    vx: sweepVx,
    blinkPeriodMs,
    blinkDutyOn,
  };

  return [beam];
}

/** Attack #13 — vertical sweeping laser, L→R, blinks off on a beat. */
export const LASER_SWEEP_LR: Pattern = {
  id: 'LASER_SWEEP_LR',
  family: 'lasers',
  spawn: (ctx) => makeLaserSweep(ctx, true),
};

/** Attack #14 — vertical sweeping laser, R→L, blinks off on a beat. */
export const LASER_SWEEP_RL: Pattern = {
  id: 'LASER_SWEEP_RL',
  family: 'lasers',
  spawn: (ctx) => makeLaserSweep(ctx, false),
};
